import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, Download, LogOut, Repeat, Coins, AlertTriangle, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useLanguage } from "../contexts/LanguageContext";
import api, { API_BASE, formatApiError } from "../lib/api";

const TOKEN_KEY = "fcs_donor_token";
const EMAIL_KEY = "fcs_donor_email";

const DonorPortal = () => {
  const { t, lang } = useLanguage();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || "");
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Magic link UI state
  const [requestEmail, setRequestEmail] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    document.title = lang === "en" ? "My donor space – Fondation Cœur Solidaire" : "Mon espace donateur – Fondation Cœur Solidaire";
  }, [lang]);

  // Consume magic-link token on landing
  useEffect(() => {
    const t = params.get("token");
    if (t) {
      (async () => {
        try {
          const { data } = await api.post("/donor/consume-link", { token: t });
          localStorage.setItem(TOKEN_KEY, data.access_token);
          localStorage.setItem(EMAIL_KEY, data.email);
          setToken(data.access_token);
          setEmail(data.email);
          toast.success(lang === "en" ? "Welcome back!" : "Bienvenue !");
          setParams({}, { replace: true });
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail));
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDonations = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get("/donor/me", { headers: { Authorization: `Bearer ${token}` } });
      setDonations(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchDonations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setToken("");
    setEmail("");
    setDonations([]);
  };

  const requestLink = async (e) => {
    e.preventDefault();
    if (!requestEmail.trim()) return;
    setRequesting(true);
    try {
      await api.post("/donor/magic-link", { email: requestEmail.trim(), locale: lang });
      setRequested(true);
      toast.success(t.donorPortal.linkSent);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.donorPortal.error);
    } finally {
      setRequesting(false);
    }
  };

  const cancelSubscription = async (subId) => {
    if (!window.confirm(t.donorPortal.confirmCancel)) return;
    try {
      await api.post(
        `/donor/subscriptions/${subId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t.donorPortal.cancelled);
      fetchDonations();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const downloadReceipt = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/donor/receipt/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recu-don-${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error(t.donorPortal.error);
    }
  };

  const totalGiven = donations
    .filter((d) => d.status_payment === "completed")
    .reduce((s, d) => s + (d.amount || 0), 0);
  const completedCount = donations.filter((d) => d.status_payment === "completed").length;
  const activeSub = donations.find(
    (d) =>
      d.donation_type === "monthly" &&
      d.paypal_subscription_id &&
      ["completed", "ACTIVE"].includes(d.status_payment) &&
      (d.paypal_subscription_status || "ACTIVE").toUpperCase() === "ACTIVE"
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="donor-portal-page">
      <Navbar />

      <section className="pt-32 pb-12 md:pt-40 bg-gradient-to-b from-[#002B49] to-[#001F35] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #C5A059 0%, transparent 50%)" }} />
        <div className="container-narrow relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors">
            <ArrowLeft size={14} /> {t.donorPortal.back}
          </Link>
          <span className="eyebrow text-[#E8C77E]">{t.donorPortal.eyebrow}</span>
          <span className="block w-16 h-px bg-[#C5A059] mt-4 mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1]" data-testid="donor-portal-title">
            {t.donorPortal.title}
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            {token ? `${t.donorPortal.welcome} ${email}` : t.donorPortal.intro}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container-narrow max-w-3xl">
          {!token ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8 md:p-12 border border-black/5 shadow-[0_20px_60px_-30px_rgba(0,43,73,0.2)]"
            >
              {requested ? (
                <div className="text-center py-6" data-testid="donor-link-sent">
                  <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-4" />
                  <h2 className="font-serif text-2xl text-[#002B49] mb-2 font-semibold">{t.donorPortal.linkSentTitle}</h2>
                  <p className="text-[#64748B] mb-2">{t.donorPortal.linkSentText}</p>
                  <button onClick={() => setRequested(false)} className="text-sm mt-4 text-[#002B49] hover:text-[#C5A059]">
                    {t.donorPortal.tryAnother}
                  </button>
                </div>
              ) : (
                <form onSubmit={requestLink} data-testid="donor-magic-link-form">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#FDF8EE] flex items-center justify-center">
                      <Mail size={18} className="text-[#C5A059]" />
                    </div>
                    <h2 className="font-serif text-2xl text-[#002B49] font-semibold">{t.donorPortal.formTitle}</h2>
                  </div>
                  <p className="text-[#64748B] text-sm mb-6">{t.donorPortal.formSubtitle}</p>
                  <input
                    type="email"
                    required
                    placeholder={t.donorPortal.emailPlaceholder}
                    value={requestEmail}
                    onChange={(e) => setRequestEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg outline-none focus:border-[#002B49] focus:ring-2 focus:ring-[#002B49]/10"
                    data-testid="donor-email-input"
                  />
                  <button
                    type="submit"
                    disabled={requesting}
                    className="btn-primary mt-4 w-full justify-center disabled:opacity-60"
                    data-testid="donor-magic-link-submit"
                  >
                    {requesting ? "..." : t.donorPortal.sendLink} <Send size={14} className="ml-2" />
                  </button>
                  <p className="text-xs text-[#64748B] mt-4 text-center">{t.donorPortal.privacyNote}</p>
                </form>
              )}
            </motion.div>
          ) : (
            <div data-testid="donor-portal-authenticated">
              <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <div className="text-sm text-[#64748B]">
                  {t.donorPortal.connectedAs} <span className="font-medium text-[#002B49]">{email}</span>
                </div>
                <button onClick={logout} className="text-sm inline-flex items-center gap-1.5 text-[#64748B] hover:text-[#002B49]" data-testid="donor-logout">
                  <LogOut size={14} /> {t.donorPortal.logout}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <KpiCard label={t.donorPortal.kpis.total} value={`${totalGiven.toFixed(2)} €`} Icon={Coins} />
                <KpiCard label={t.donorPortal.kpis.count} value={completedCount} Icon={Repeat} />
                <KpiCard
                  label={t.donorPortal.kpis.activeSub}
                  value={activeSub ? `${activeSub.amount.toFixed(2)} €/mo` : "—"}
                  Icon={Repeat}
                  highlight={!!activeSub}
                />
              </div>

              {activeSub && (
                <div className="bg-[#FDF8EE] border border-[#C5A059]/40 rounded-xl p-5 mb-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between" data-testid="donor-active-sub">
                  <div>
                    <div className="font-serif text-lg text-[#002B49] font-semibold">{t.donorPortal.activeSubTitle}</div>
                    <div className="text-sm text-[#64748B] mt-1">{t.donorPortal.activeSubText}</div>
                  </div>
                  <button
                    onClick={() => cancelSubscription(activeSub.paypal_subscription_id)}
                    className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-red-200 text-red-700 hover:bg-red-50"
                    data-testid="donor-cancel-sub"
                  >
                    <AlertTriangle size={14} /> {t.donorPortal.cancelSub}
                  </button>
                </div>
              )}

              <h2 className="font-serif text-2xl text-[#002B49] font-semibold mb-4">{t.donorPortal.historyTitle}</h2>
              <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                {loading ? (
                  <div className="p-8 text-center text-[#64748B]">…</div>
                ) : donations.length === 0 ? (
                  <div className="p-12 text-center text-[#64748B]" data-testid="donor-no-donations">
                    {t.donorPortal.empty}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#FDFBF7] border-b border-black/5">
                        <tr className="text-left text-[#64748B] uppercase text-[11px] tracking-wider">
                          <th className="px-5 py-3 font-medium">{t.donorPortal.cols.date}</th>
                          <th className="px-5 py-3 font-medium">{t.donorPortal.cols.amount}</th>
                          <th className="px-5 py-3 font-medium">{t.donorPortal.cols.type}</th>
                          <th className="px-5 py-3 font-medium">{t.donorPortal.cols.status}</th>
                          <th className="px-5 py-3 font-medium text-right">{t.donorPortal.cols.actions}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donations.map((d, i) => (
                          <tr key={d.id} className="border-b border-black/5 last:border-0 hover:bg-[#FDFBF7]" data-testid={`donor-row-${i}`}>
                            <td className="px-5 py-3 text-[#64748B] whitespace-nowrap">
                              {new Date(d.created_at).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR")}
                            </td>
                            <td className="px-5 py-3 font-serif text-base font-semibold text-[#002B49]">
                              {d.amount.toFixed(2)} {d.currency === "EUR" ? "€" : d.currency === "USD" ? "$" : "£"}
                            </td>
                            <td className="px-5 py-3">
                              <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium ${d.donation_type === "monthly" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>
                                {d.donation_type === "monthly" ? t.donorPortal.monthly : t.donorPortal.oneTime}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-xs text-[#64748B] capitalize">{d.status_payment}</td>
                            <td className="px-5 py-3 text-right">
                              {d.status_payment === "completed" && (
                                <button
                                  onClick={() => downloadReceipt(d.id)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#002B49] hover:text-[#C5A059]"
                                  data-testid={`donor-receipt-${i}`}
                                >
                                  <Download size={12} /> {t.donorPortal.downloadReceipt}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const KpiCard = ({ label, value, Icon, highlight }) => (
  <div className={`rounded-2xl border p-5 ${highlight ? "bg-[#FDF8EE] border-[#C5A059]/40" : "bg-white border-black/5"}`}>
    <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-medium">{label}</div>
    <div className="flex items-end justify-between mt-2">
      <div className="font-serif text-2xl text-[#002B49] font-semibold">{value}</div>
      <Icon size={18} className="text-[#C5A059]" />
    </div>
  </div>
);

export default DonorPortal;
